// @file Surface map store
import device from '@@/bits/device'
import type { LatLng, OverlappingMarkerSpiderfier } from '@@/bits/map'
import { getVuexStore } from '@@/bits/pinia'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Cid, Id, LocationMenuPlaceInfo, PinDropSuggestion } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource, JsonAPIResponse, WallTheme } from '@padlet/arvo'
import { fetchMapThemes as arvoFetchMapThemes } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface PostLocation {
  postCid: Cid | null
  latLng: LatLng | null
  name: string
  sectionId?: Id
}

export const useSurfaceMapStore = defineStore('surfaceMap', () => {
  const surfaceStore = useSurfaceStore()
  const windowSizeStore = useWindowSizeStore()

  /* --------------------------------------------- */
  /* FETCH & SHOW MAP THEME                        */
  /* --------------------------------------------- */
  const map = computed<google.maps.Map | null | undefined>(() => getVuexStore<RootState>()?.getters['map/map'])
  const mapThemes = ref<Record<number, WallTheme>>()
  const isFetchingMapThemes = ref(false)
  const currentThemeId = computed<number>(() => getVuexStore<RootState>()?.getters.themeId)
  const currentMapTheme = computed(() =>
    mapThemes.value !== undefined ? mapThemes.value[currentThemeId.value] : undefined,
  )
  const isMapThemeChanged = computed<boolean>(() => currentThemeId.value !== surfaceStore.mapThemeId)

  const setMapBeingUsed = ({ map: updatedMap }: { map: google.maps.Map }): void => {
    void getVuexStore<RootState>()?.dispatch('map/setMapBeingUsed', { map: updatedMap })
  }

  async function fetchMapThemes(): Promise<void> {
    isFetchingMapThemes.value = true
    const themesResponse: JsonAPIResponse<WallTheme> = await arvoFetchMapThemes()
    if (themesResponse.data != null) {
      const mapThemeDictionary = {}
      const allThemes = themesResponse.data as Array<JsonAPIResource<WallTheme>>
      allThemes.forEach((mapTheme: JsonAPIResource<WallTheme>): void => {
        const details = mapTheme.attributes
        mapThemeDictionary[details.id] = details
      })
      mapThemes.value = mapThemeDictionary
    }
    isFetchingMapThemes.value = false
  }

  /* --------------------------------------------- */
  /* PICKING LOCATIONS                             */
  /* --------------------------------------------- */
  const isPickingLocation = computed<boolean>(() => getVuexStore<RootState>()?.getters['map/isPickingLocation'])
  const postWithLocationBeingEditedCid = computed<Cid | null>(
    () => getVuexStore<RootState>()?.getters['map/postWithLocationBeingEditedCid'],
  )
  const sectionIdOfPostBeingAdded = computed<Id | null>(
    () => getVuexStore<RootState>()?.getters['map/sectionIdOfPostBeingAdded'],
  )

  const hidePickLocationPanel = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/hidePickLocationPanel')
  }

  function startPickingLocation(payload?: { postCid?: Cid; sectionId?: Id }): void {
    void getVuexStore<RootState>()?.dispatch('map/startPickingLocation', payload)
  }

  const stopPickingLocation = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/stopPickingLocation')
  }

  const pickLocation = (payload: Partial<PostLocation>): void => {
    void getVuexStore<RootState>()?.dispatch('map/pickLocation', payload)
  }

  const pickLocationAndCreateNewPost = (payload: Omit<PostLocation, 'postCid'>): void => {
    void getVuexStore<RootState>()?.dispatch('map/pickLocationAndCreateNewPost', payload)
  }

  const pickLocationAndUpdateLocation = (payload: PostLocation): void => {
    void getVuexStore<RootState>()?.dispatch('map/pickLocationAndUpdateLocation', payload)
  }

  /* --------------------------------------------- */
  /* SHOW MAP POST, MAKRER AND PREVIEW PANEL       */
  /* --------------------------------------------- */
  const mapDoesFitMarkersBounds = computed<boolean>(() => getVuexStore<RootState>()?.getters['map/mapDoesFitMarkersBounds'])
  const pinDropSuggestions = computed<PinDropSuggestion[] | null>(
    () => getVuexStore<RootState>()?.getters['map/pinDropSuggestions'],
  )
  const pinDropLocation = computed<google.maps.LatLng | null>(() => getVuexStore<RootState>()?.getters['map/pinDropLocation'])
  const postWithPopupBeingOpenedCid = computed<Cid | null>(
    () => getVuexStore<RootState>()?.getters['map/postWithPopupBeingOpenedCid'],
  )
  const postWithMarkerHighlightedCid = computed<Cid | null>(
    () => getVuexStore<RootState>()?.getters['map/postWithMarkerHighlightedCid'],
  )
  const overlappingMarkerSpiderfier = computed<OverlappingMarkerSpiderfier | null>(
    () => getVuexStore<RootState>()?.getters['map/overlappingMarkerSpiderfier'],
  )
  const isPreviewPanelShown = computed<boolean>(() => getVuexStore<RootState>()?.getters['map/isPreviewPanelShown'])

  const setMarkerOverlayLayer = (payload: { spiderfier: OverlappingMarkerSpiderfier }): void => {
    void getVuexStore<RootState>()?.dispatch('map/setMarkerOverlayLayer', payload)
  }

  function openPostPopup(payload: { postCid: Cid | null }): void {
    void getVuexStore<RootState>()?.dispatch('map/openPostPopup', payload)
  }

  const closePostPopup = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/closePostPopup')
  }

  const highlightPostMarker = (payload: { postCid: Cid | null }): void => {
    void getVuexStore<RootState>()?.dispatch('map/highlightPostMarker', payload)
  }

  const toggleMapPreviewPanel = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/toggleMapPreviewPanel')
  }

  const enableMapFittingMarkersBounds = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/enableMapFittingMarkersBounds', true)
  }

  const disableMapFittingMarkersBounds = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/disableMapFittingMarkersBounds', false)
  }

  const setMapPinDropSuggestions = ({ suggestions, location }): void => {
    void getVuexStore<RootState>()?.dispatch('map/setMapPinDropSuggestions', { suggestions, location })
  }

  const clearMapPinDropSuggestions = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/clearMapPinDropSuggestions')
  }

  /* --------------------------------------------- */
  /* MAP RELATED POSITIONING                       */
  /* --------------------------------------------- */
  interface MapPopupOffsetStyle {
    top: number
    bottom: number
    left: number
    right: number
  }

  const offsetForMapPopup = computed<MapPopupOffsetStyle>(() => getVuexStore<RootState>()?.getters['map/offsetForMapPopup'])

  const postListPosition = computed<'side' | 'bottom'>(() =>
    windowSizeStore.isSmallerThanTabletLandscape || device.app || surfaceStore.isEmbedded ? 'bottom' : 'side',
  )
  /* --------------------------------------------- */
  /* SAFE STORAGE BASED RECENT SEARCHES            */
  /* --------------------------------------------- */
  const recentLocationMenuSearches = computed<LocationMenuPlaceInfo[]>(() =>
    getVuexStore<RootState>()?.getters['map/recentLocationMenuSearches'].reverse(),
  )

  const saveRecentlySearchedLocations = (payload: { newPlace: LocationMenuPlaceInfo }): void => {
    void getVuexStore<RootState>()?.dispatch('map/saveRecentlySearchedLocations', payload)
  }

  const syncRecentlySearchedLocationsFromStorage = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/syncRecentlySearchedLocationsFromStorage')
  }

  /* --------------------------------------------- */
  /* DRAG & DROP                                   */
  /* --------------------------------------------- */
  const isDraggingPosts = computed<boolean>(() => getVuexStore<RootState>()?.getters['map/isDraggingPosts'])
  const isDraggingLocationPickerPin = computed<boolean>(
    () => getVuexStore<RootState>()?.getters['map/isDraggingLocationPickerPin'],
  )

  function startDraggingPosts(): void {
    void getVuexStore<RootState>()?.dispatch('map/startDraggingPosts')
  }

  function stopDraggingPosts(): void {
    void getVuexStore<RootState>()?.dispatch('map/stopDraggingPosts')
  }

  const startDraggingLocationPickerPin = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/startDraggingLocationPickerPin')
  }

  const stopDraggingLocationPickerPin = (): void => {
    void getVuexStore<RootState>()?.dispatch('map/stopDraggingLocationPickerPin')
  }
  return {
    // state
    mapThemes,
    isFetchingMapThemes,
    currentThemeId,
    isMapThemeChanged,
    currentMapTheme,
    postListPosition,
    mapDoesFitMarkersBounds,
    map,
    pinDropSuggestions,
    pinDropLocation,
    isPickingLocation,
    postWithPopupBeingOpenedCid,
    postWithLocationBeingEditedCid,
    postWithMarkerHighlightedCid,
    sectionIdOfPostBeingAdded,
    overlappingMarkerSpiderfier,
    offsetForMapPopup,
    isPreviewPanelShown,
    recentLocationMenuSearches,
    isDraggingLocationPickerPin,
    isDraggingPosts,

    // actions
    fetchMapThemes,
    startDraggingPosts,
    stopDraggingPosts,
    enableMapFittingMarkersBounds,
    disableMapFittingMarkersBounds,
    setMapBeingUsed,
    setMapPinDropSuggestions,
    clearMapPinDropSuggestions,
    hidePickLocationPanel,
    startPickingLocation,
    stopPickingLocation,
    pickLocation,
    pickLocationAndCreateNewPost,
    pickLocationAndUpdateLocation,
    setMarkerOverlayLayer,
    openPostPopup,
    closePostPopup,
    highlightPostMarker,
    toggleMapPreviewPanel,
    saveRecentlySearchedLocations,
    syncRecentlySearchedLocationsFromStorage,
    startDraggingLocationPickerPin,
    stopDraggingLocationPickerPin,
  }
})
